import { baseURL, protectedAxiosInstance, globalAxiosInstance } from "./axios";
import type {
  ConnectPipecatSessionRequest,
  CreatePipecatSessionRequest,
  PipecatConnectResponse,
  PipecatStartResponse,
} from "@/types/roleplay-session";
import { type VideoModuleData, type Faq } from "@/helper/types";
import type {
  MpleAvatarBackend,
  MpleAvatarEndReason,
  MpleAvatarSessionSource,
} from "@/helper/mpleAvatar";
import type { InteractionData } from "@/modules/videoPageModule/_hooks/useVideoChat";
import { USE_GCS } from "./unProtectedApi";

const shouldUpdateLastActivity = (updateLastActivity?: boolean) => {
  if (localStorage.getItem("isImpersonationLogin") === "true") {
    return false;
  }

  const isOrgPresent = localStorage.getItem("orgName") !== null;
  return updateLastActivity !== undefined ? updateLastActivity : isOrgPresent;
};

export const getBookDataApi = async (id: string) => {
  return await protectedAxiosInstance.get(`/users/book-details/${id}`);
};

export const warmupFlaskApi = () => {
  return protectedAxiosInstance.post("/users/warmup-flask");
};

export const getBookDataApiV2 = async (id: string, filter: any) => {
  return await protectedAxiosInstance.get(
    `/users/book-details/v2/${id}?page=${filter.page}&limit=${filter.limit}`
  );
};

export const shareModuleApi = (body: {
  chapterId: string;
  trainingId: string;
  email?: string;
  phone?: string;
}) => {
  return protectedAxiosInstance.post("/shortlinks/share-module", body);
};

export const listSharedModulesApi = (chapterId: string, trainingId: string) => {
  return protectedAxiosInstance.get("/shortlinks/share-module/list", {
    params: { chapterId, trainingId },
  });
};

export const getBookDataApiV4 = async (
  id: string,
  filter: any,
  learningPathId?: string,
  onBoardingBatchId?: string
) => {
  const params: any = {
    ...filter,
    ...(learningPathId && { lpId: learningPathId }),
    ...(onBoardingBatchId && { onBoardBatchId: onBoardingBatchId }),
  };

  return protectedAxiosInstance.get(`/users/book-details/v4/${id}`, {
    params,
  });
};

export const getChapterAttemptDetailsApi = async (data: {
  userId: string;
  bookId: string;
  chapterId: string;
}) => {
  return await protectedAxiosInstance.post(
    `/users/chapter-attempt-details`,
    data
  );
};

export const getChapterDataApi = async (data: any) => {
  return await protectedAxiosInstance.post(`/users/chapter-details`, data);
};

export const getChapter = async (chapterId: string) => {
  return await protectedAxiosInstance.get(`/chapters/${chapterId}`);
};

export const validateRoleplayGeolocationApi = (body: {
  chapterId: string;
  latitude: number;
  longitude: number;
  accuracy: number;
  monitor?: boolean;
}) => protectedAxiosInstance.post("/roleplay/geolocation/validate", body);

export const recordRoleplayGeolocationViolationApi = (body: {
  resultId: string;
  outsideStartedAt: string;
  distanceMeters: number;
  latitude: number;
  longitude: number;
  accuracy: number;
}) => protectedAxiosInstance.post("/roleplay/geolocation/violation", body);

export const recordRoleplayGeolocationPermissionDeniedApi = (body: {
  resultId: string;
}) =>
  protectedAxiosInstance.post("/roleplay/geolocation/permission-denied", body);

export const initiateAssessmentVideoUpload = () => {
  return protectedAxiosInstance.post<{
    uploadId: string;
    key: string;
    filePath: string;
  }>(
    USE_GCS
      ? `/gcs/media/assessment-video/initiate`
      : `/chapters/assessment-video/initiate`
  );
};

export const uploadAssessmentVideoPart = (formData: FormData) => {
  return protectedAxiosInstance.post<{ ETag?: string }>(
    USE_GCS
      ? `/gcs/media/assessment-video/part`
      : `/chapters/assessment-video/part`,
    formData
  );
};

export const completeAssessmentVideoUpload = (body: {
  uploadId: string;
  key: string;
  parts: { ETag: string; PartNumber: number }[];
}) => {
  return protectedAxiosInstance.post(
    USE_GCS
      ? `/gcs/media/assessment-video/complete`
      : `/chapters/assessment-video/complete`,
    body
  );
};

export const abortAssessmentVideoUpload = (body: {
  uploadId: string;
  key: string;
}) => {
  return protectedAxiosInstance.post(
    USE_GCS
      ? `/gcs/media/assessment-video/abort`
      : `/chapters/assessment-video/abort`,
    body
  );
};

export async function getVideoData(
  bookId: string,
  chapterId: string
): Promise<{
  code: number;
  data: VideoModuleData;
  error: string;
  message: string;
}> {
  const response = await protectedAxiosInstance.post(`/users/video`, {
    bookId,
    chapterId,
  });

  return response.data;
}

// Fetches the distinct book categories for the org, optionally narrowed by a
// case-insensitive prefix `search` (used by the category dropdown's search
// box) and capped at `limit` results (backend clamps this to 50).
// Unwraps to `response.data` directly, so callers get `{ data, total, limit }`
// instead of the raw axios response.
export const getAllCategories = async (params?: {
  search?: string;
  limit?: number;
}) => {
  const response = await protectedAxiosInstance.get(`/books/categories`, {
    params,
  });
  return response.data;
};

export const getBooksCompletionV2 = async (params: {
  page: number;
  limit: number;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  categories?: string[];
  // true returns category sections for the All trainings view.
  grouped?: boolean;
  // Used only with grouped=true. This pages category sections, not trainings.
  categoryPage?: number;
  // Used only with grouped=true. Number of category sections to load.
  categoryLimit?: number;
}) => {
  try {
    const response = await protectedAxiosInstance.post(
      "/books/get-books-completionV2",
      params
    );
    return response.data;
  } catch (error: any) {
    console.error("getBooksCompletionV2 error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    throw new Error(
      error.response?.data?.message || "Failed to fetch books completionV2"
    );
  }
};

// GET /books/v1/home-grouped
// Used by the home "All" tab to load paginated category sections.
export const getHomeGrouped = async (params: {
  search?: string;
  categoryPage?: number;
  categoryLimit?: number;
}) => {
  try {
    const response = await protectedAxiosInstance.get(
      "/books/v1/home-grouped",
      { params }
    );
    return response.data;
  } catch (error: any) {
    console.error("getHomeGrouped error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw new Error(
      error.response?.data?.message || "Failed to fetch home grouped books"
    );
  }
};

// GET /books/category-books
// Used by the category filter view (home) and the "see all" category page.
export const getCategoryBooks = async (params: {
  category: string;
  page?: number;
  limit?: number;
  search?: string;
}) => {
  try {
    const response = await protectedAxiosInstance.get("/books/category-books", {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error("getCategoryBooks error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw new Error(
      error.response?.data?.message || "Failed to fetch category books"
    );
  }
};

export const getBookDetails = async (id: any) => {
  return await protectedAxiosInstance.get(`/books/${id}`);
};

export const addBookApi = async (body: { scratchCode: string }) => {
  const response = await protectedAxiosInstance.post(`/users/addBook`, body);
  return response;
};

export const getAssessmentDataApi = async (
  bookId: string,
  chapterId: string,
  language?: string
) => {
  const response = await protectedAxiosInstance.post(`/chapters/assessment`, {
    bookId,
    chapterId,
    ...(language ? { language } : {}),
  });
  return response;
};

type answers = {
  questionNo: number;
  answer: string[];
};

export const submitAssessmentDataApi = async (
  bookId: string,
  chapterId: string,
  answers: answers[],
  exitedFullScreen?: boolean,
  browserSwitched?: boolean,
  language?: string,
  multipleScreensDetected?: boolean,
  resultVideo?: string,
  learningPathId?: string,
  onBoardingBatchId?: string
) => {
  const response = await protectedAxiosInstance.post(
    `/chapters/submit-assessment`,
    {
      bookId,
      chapterId,
      answers,
      exitedFullScreen,
      browserSwitched,
      multipleScreensDetected,
      ...(language ? { language } : {}),
      ...(resultVideo ? { resultVideo } : {}),
      ...(learningPathId ? { learningPathId } : {}),
      ...(onBoardingBatchId ? { onBoardingBatchId } : {}),
    }
  );
  return response;
};

export const getAssessmentLang = (chapterId: string) => {
  return protectedAxiosInstance.get(`/chapters/assessment-lang/${chapterId}`);
};

export const getAssessmentHistoryByChapterApi = async (chapterId: string) => {
  const response = await protectedAxiosInstance.get(
    `assesment-results/fetch-by-chapter/`,
    { params: { id: chapterId } }
  );
  return response;
};

export const createRoleplayHistoryByChapter = (body: any) => {
  return protectedAxiosInstance.post(`roleplay/create-result`, body);
};

export const createSessionLog = (body: any) => {
  return globalAxiosInstance.post(`/roleplay/session-log`, body);
};

export const addSessionLogErrorApi = (body: {
  resultId: string;
  errorMessage?: string;
  errorCode?: string;
}) => {
  return globalAxiosInstance.post(`/roleplay/session-log-error`, body);
};

export const checkLimitExceededOrNot = () => {
  return protectedAxiosInstance.get(`users/check-limit`);
};

export const increaseCoachUsage = () => {
  return protectedAxiosInstance.get(`users/increase-coach-usage/`);
};

export const increaseSessinUsage = (
  type: "coach" | "roleplayMinutes",
  resultId?: string
) => {
  return protectedAxiosInstance.post(`users/increase-session-usage/`, {
    type,
    resultId,
  });
};

export const isRoleplayCompleted = (resultId: string) => {
  return protectedAxiosInstance.get(`roleplay/completed/${resultId}`);
};

export const saveRoleplayHistoryByChapter = (resultId: string, body: any) => {
  return protectedAxiosInstance.put(`roleplay/save-result/${resultId}`, body);
};

export const savePptV3QuestionResponse = (resultId: string, body: any) => {
  return protectedAxiosInstance.post(
    `roleplay/ppt-v3/question-response/${resultId}`,
    body
  );
};

export const getRoleplayByChapterApi = (chapterId: string) => {
  return protectedAxiosInstance.get(`roleplay/by-chapter/${chapterId}`);
};

/** The AI's opening line for the behaviour the learner picked (null when nothing changes). */
export const regenerateRoleplayAiIntroApi = (
  chapterId: string,
  behaviour: string
) => {
  return protectedAxiosInstance.post<{
    data: { aiIntro: string | null; regenerated: boolean };
  }>(`roleplay/ai-intro/${chapterId}`, { behaviour });
};

/** Pipecat roleplay, start modal open: what the browser needs to connect to the bot. */
export async function connectPipecatRoleplaySessionApi(
  body: ConnectPipecatSessionRequest
): Promise<PipecatConnectResponse> {
  const { data } = await protectedAxiosInstance.post<PipecatConnectResponse>(
    "roleplay/v2/sessions/connect",
    body
  );
  return data;
}

/** Pipecat roleplay, Start: create the attempt and get the start token. */
export async function createPipecatRoleplaySessionApi(
  body: CreatePipecatSessionRequest
): Promise<PipecatStartResponse> {
  const { data } = await protectedAxiosInstance.post<PipecatStartResponse>(
    "roleplay/v2/sessions",
    body
  );
  return data;
}

export const getRoleplayHistoryByChapter = (
  chapterId: string,
  limit?: number
) => {
  return protectedAxiosInstance.get(`roleplay/result-history`, {
    params: { chapterId, ...(limit !== undefined && { limit }) },
  });
};

export const getRoleplayHistoryFilter = (filter: any) => {
  return protectedAxiosInstance.post(`roleplay/result-history-filter/`, {
    filter,
  });
};

export const getAssessmentHistoryResultByChapterApi = async (
  testId: string
) => {
  const response = await protectedAxiosInstance.get(
    `assesment-results/fetch/`,
    { params: { id: testId } }
  );
  return response;
};

export const getProfileDetailsApi = async (updateLastActivity?: boolean) => {
  if (typeof window !== "undefined" && !localStorage.getItem("token")) {
    return { data: { code: 200, data: null } };
  }
  const finalUpdateFlag = shouldUpdateLastActivity(updateLastActivity);

  const response = await protectedAxiosInstance.get(`/users/get-user-profile`, {
    params: { updateLastActivity: String(finalUpdateFlag) },
  });
  return response;
};

export const getTrainingBannersApi = () =>
  protectedAxiosInstance.get<string[]>(`/organizations/training-banners`);

export const getGlobalBotDetails = async () => {
  const response = await protectedAxiosInstance.get(`/chat/get-bot-details`);
  return response;
};

export const getCertificate = async (bookId: any) => {
  try {
    if (!bookId) {
      throw new Error("bookId is required.");
    }
    const response = await protectedAxiosInstance.get(
      `/user-interview/get-user-certificate`,
      {
        params: { bookId },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Axios Error:", error.response?.data || error.message);
    throw error;
  }
};

export const getPresignedUrlApi = async (body: any) => {
  const response = await protectedAxiosInstance.post(
    USE_GCS
      ? `/gcs/media/user/upload-signed-url`
      : `/users/get-upload-signed-url`,
    body
  );
  return response;
};

export const updateUserProfileApi = async (body: any) => {
  const response = await protectedAxiosInstance.post(
    `/users/update-user-profile`,
    body
  );
  return response;
};

//user assessments api
export const getEvaluations = async (body: any) => {
  const params: any = { asUser: true };

  if (body?.bookId) {
    params.trainingId = body?.bookId;
  }

  if (body?.page) {
    params.page = body?.page;
  }
  if (body?.limit) {
    params.limit = body?.limit;
  }

  if (body?.showTrainingDetails) {
    params.showTrainingDetails = body?.showTrainingDetails;
  }
  if (body?.status) {
    params.status = body?.status;
  }

  return protectedAxiosInstance.get(`/user-interview/knowledgeEvaluations/`, {
    params,
  });
};

export const getPendingTasksFromAPI = async () => {
  return protectedAxiosInstance.get(`/users/tasks/pending-tasks`);
};

export const getEvaluationsTraining = async (body: any) => {
  const params: any = { asUser: true };

  if (body?.bookId) {
    params.trainingId = body?.bookId;
  }

  return protectedAxiosInstance.get(`/user-interview/evaluations/training`, {
    params,
  });
};

export const getEvaluationsTrainingHistory = async (body: any) => {
  const params: any = { asUser: true };

  if (body?.bookId) {
    params.trainingId = body?.bookId;
  }

  if (body?.moduleId) {
    params.moduleId = body?.moduleId;
  }

  if (body?.page) {
    params.page = body?.page;
  }
  if (body?.limit) {
    params.limit = body?.limit;
  }
  if (body?.showTrainingDetails) {
    params.showTrainingDetails = body?.showTrainingDetails;
  }
  if (body?.status) {
    params.status = body?.status;
  }

  if (body?.userId) {
    params.userId = body?.userId;
  }
  // training page api
  return protectedAxiosInstance.get(
    `/user-interview/evaluations/training/history`,
    {
      params,
    }
  );
};

export const getEvaluationsVideos = async (filter: any) => {
  return protectedAxiosInstance.get(
    `/user-interview/evaluations-videos?filter=${JSON.stringify(filter)}`
  );
};

export const getUserAssessmentsApi = (params: {
  page: number;
  limit: number;
  status?: string;
}) => {
  return protectedAxiosInstance.get(`/assesment-results/user-assessments`, {
    params: {
      page: params.page,
      limit: params.limit,
      ...(params.status && params.status !== "All"
        ? { status: params.status }
        : {}),
    },
  });
};

export const startUserAssessmentApi = (id: string) => {
  return protectedAxiosInstance.patch(
    `/assesment-results/user-assessments/${id}/start`
  );
};

export const submitUserAssessmentApi = (
  id: string,
  answers: any[],
  exitedFullScreen?: boolean,
  multipleScreensDetected?: boolean,
  browserSwitched?: boolean
) => {
  return protectedAxiosInstance.patch(
    `/assesment-results/user-assessments/${id}/submit`,
    { answers, exitedFullScreen, multipleScreensDetected, browserSwitched }
  );
};

export const getUserAssessmentResultApi = (id: string) => {
  return protectedAxiosInstance.get(
    `/assesment-results/user-assessments/${id}/result`
  );
};

export const getFeedVideos = async (filter: any) => {
  return protectedAxiosInstance.get(
    `/user-interview/feed-videos?filter=${JSON.stringify(filter)}`
  );
};

export const getAllMyFeeds = () => {
  return protectedAxiosInstance.get(`/feeds/`);
};

export const getVideoDetails = async (body: any) => {
  return protectedAxiosInstance.post("/user-interview/get-video", body);
};

export const updateLikes = async (body: any) => {
  return protectedAxiosInstance.post(`/user-interview/LikeUnlike`, body);
};

export const assignEvaluation = async (body: {
  trainingId: string;
  validTo: string;
  validFrom: string;
  moduleId?: string;
}) => {
  return protectedAxiosInstance.post(
    `/user-interview/assign-evaluation/`,
    body
  );
};

export const getUserDashboardMatrix = async (
  userId: string,
  userEmail: string,
  project: string,
  startDate: any,
  toDate: any,
  trainingFilter: string,
  updateLastActivity?: boolean
) => {
  // return {
  //   data: {
  //     numberOfTrainings: "-",
  //     numberOfTrainingsCompleted: "-",
  //     roleplayCardData: {
  //       totalRoleplays: "-",
  //       highestScore: "-",
  //       averageScore: "-",
  //     },
  //     evaluationCardData: { total: "-", highestScore: "-", averageScore: "-" },
  //     coachTimeCard: { coachTime: "-" },
  //     roleplayHintUsage: {
  //       totalAttempts: "-",
  //       hintUsed: "-",
  //       hintNotUsed: "-",
  //     },
  //     roleplayAndEvaluationCount: {
  //       roleplaysData: {
  //         week: {
  //           labels: [
  //             "03 Sep",
  //             "04 Sep",
  //             "05 Sep",
  //             "06 Sep",
  //             "07 Sep",
  //             "08 Sep",
  //             "09 Sep",
  //           ],
  //           values: [0, 0, 0, 0, 0, 0, 0],
  //         },
  //         month: {
  //           labels: [
  //             "03 Sep",
  //             "04 Sep",
  //             "05 Sep",
  //             "06 Sep",
  //             "07 Sep",
  //             "08 Sep",
  //             "09 Sep",
  //           ],
  //           values: [0, 0, 0, 0, 0, 0, 0],
  //         },
  //       },
  //       evaluationsData: {
  //         week: {
  //           labels: [
  //             "03 Sep",
  //             "04 Sep",
  //             "05 Sep",
  //             "06 Sep",
  //             "07 Sep",
  //             "08 Sep",
  //             "09 Sep",
  //           ],
  //           values: [0, 0, 0, 0, 0, 0, 0],
  //         },
  //         month: {
  //           labels: [
  //             "3 Sep",
  //             "4 Sep",
  //             "5 Sep",
  //             "6 Sep",
  //             "7 Sep",
  //             "8 Sep",
  //             "9 Sep",
  //           ],
  //           values: [0, 0, 0, 0, 0, 0, 0],
  //         },
  //       },
  //     },
  //     projection: "all",
  //     evaluationScore: [],
  //     roleplaySkillScore: [],
  //     evaluationSkillScore: [],
  //   },
  // };
  return protectedAxiosInstance.post(`/dashboard/get-dashboard-data`, {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userId,
    userEmail,
    project,
    startDate,
    toDate,
    trainingFilter,
    updateLastActivity: shouldUpdateLastActivity(updateLastActivity),
  });
};

export const getManagerLeaderboardData = async (body: any) => {
  return protectedAxiosInstance.post(
    `/organizations/get-manager-leaderboard-data`,
    body
  );
};

export const getUserSkillsdata = async (body: any) => {
  return protectedAxiosInstance.post(`/users/get-user-skills`, body);
};

export const getManagerAllEmpSkills = async (body: any) => {
  return protectedAxiosInstance.post(
    `/users/fetch-all-emp-skills-manager-dashboard`,
    body
  );
};

export const getEngagementChartData = async (body: any) => {
  return protectedAxiosInstance.post(`dashboard/manager/engagement-overview`, {
    ...body,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

export const getManagerDashboardData = async (body: any) => {
  return protectedAxiosInstance.post(`/users/get-manager-dashboard-data`, body);
};

export const getUserLearningPatterns = async (body: any) => {
  return protectedAxiosInstance.post(
    `/dashboard/training/user-learning-patterns`,
    body
  );
};

export const fetchNotifications = async () => {
  return protectedAxiosInstance.get(`/users/get-notifications`);
};

export const fetchRoleplayResults = async (id: string) => {
  return protectedAxiosInstance.get(`/roleplay/get-result/${id}`);
};

export const fetchPeersMedia = async (chapterId: string) => {
  return protectedAxiosInstance.get(`/roleplay/peers-media/${chapterId}`);
};

export const getRoleplayHistoryUser = async (body: any) => {
  return protectedAxiosInstance.post(`/roleplay/by-user`, body);
};

export const getDictionaryWords = async (params: any) => {
  // const params = {
  //   search: filter?.search || "",
  //   limit: filter.limit,
  //   page: filter.page,
  // };
  return protectedAxiosInstance.get(`/dictionary`, { params });
};

export const getPdfandvideoLink = async (userId: string) => {
  return protectedAxiosInstance.get(`/user-books/${userId}/media`);
};
export const sendDocuments = async (body: any) => {
  return protectedAxiosInstance.post(`/doctors/send-documents`, body);
};

export function getDoctors(orgId: string, body: any) {
  return protectedAxiosInstance.post(`/doctors/getAll/${orgId}`, body);
}

export const changeUserPassword = async (body: {
  newPassword: string;
  prevPassword: string;
  email: string;
}) => {
  return protectedAxiosInstance.post("/users/change-password", body);
};

export const unlockRolePlay = async (body: { moduleId: string }) => {
  return protectedAxiosInstance.post("/users/unlock-module", body);
};

export const getAssignRoleplay = async (filter: any = {}) => {
  return protectedAxiosInstance.get("roleplay/get-roleplay-result", {
    params: filter,
  });
};

export const getUserRewards = async (filter: any = {}) => {
  return protectedAxiosInstance.post("rewards/get-rewards", filter);
};

export const getBadgeResults = async () => {
  return protectedAxiosInstance.get("roleplay/get-result-badges");
};

export const getUserLeaderBoard = async (filter: any = {}) => {
  return protectedAxiosInstance.post("organizations/get-leaderboard", filter);
};

export const getEvaluation = async (evaluationId: string) => {
  return protectedAxiosInstance.get(
    `/user-interview/evaluation/${evaluationId}`
  );
};

export const getEvaluationResult = async (
  evaluationId: string,
  chapterId?: any,
  startFromModule?: boolean
) => {
  return protectedAxiosInstance.get(
    `/user-interview/evaluation-results/${evaluationId}`,
    {
      params: { chapterId, startFromModule },
    }
  );
};

export const getEvaluationStatus = async (evaluationId: string) => {
  return protectedAxiosInstance.get(
    `/user-interview/evaluation-status/${evaluationId}`
  );
};

export const getInterviewResult = async (interviewId: string) => {
  return protectedAxiosInstance.get(`/user-interview/results/${interviewId}`);
};

export const getUnseenNotificationsCountApi = (
  updateLastActivity?: boolean
) => {
  if (typeof window !== "undefined" && !localStorage.getItem("token")) {
    return Promise.resolve({ data: { code: 200, data: 0 } });
  }
  const finalUpdateFlag = shouldUpdateLastActivity(updateLastActivity);

  return protectedAxiosInstance.get("/users/get-unseen-notifications-count", {
    params: { updateLastActivity: String(finalUpdateFlag) },
  });
};

export const markNotificationsAsSeenApi = () => {
  return protectedAxiosInstance.post("/users/mark-notifications-seen");
};

export const saveDeviceToken = (body: { token: string; email: string }) => {
  return protectedAxiosInstance.post(`/notification/save-device-token`, body);
};

export const comleteTraining = (body: { trainingId: string }) => {
  return protectedAxiosInstance.post(`/users/complete-training`, body);
};

export const reverseComletedTraining = (body: { trainingId: string }) => {
  return protectedAxiosInstance.post(`/users/reverse-completed-training`, body);
};

export const downloadResults = async (
  currentUrl: string,
  orgLogoUrl?: string
) => {
  return protectedAxiosInstance.post("/roleplay/pdf-url-data/", {
    currentUrl,
    orgLogoUrl,
  });
};

export const updateRoleplayResult = async (id: string, body: any) => {
  return protectedAxiosInstance.patch(`/roleplay/roleplay-result/${id}`, body);
};

export const updateRoleplayResultData = (data: any) => {
  return protectedAxiosInstance.post("roleplay/update-roleplay-result", {
    data,
  });
};

export const findRoleplayResult = async (body: any) => {
  return protectedAxiosInstance.post("/roleplay/roleplay-result", body);
};
export const postIssue = async (body: any) => {
  return protectedAxiosInstance.post("/users/post-issue/", body);
};

export const getIssues = async () => {
  return protectedAxiosInstance.get("/users/get-issues/");
};

export function updateIssue(ticketNumber: string, body: any) {
  return protectedAxiosInstance.patch(
    `/users/update-issue/${ticketNumber}`,
    body
  );
}

export function refrehConversationApi(ticketNumber: string) {
  return protectedAxiosInstance.get(`/users/get-issue/${ticketNumber}`);
}

export function getActivity(activityId: string) {
  return protectedAxiosInstance.get(`/activities/get-activity/${activityId}`);
}

export function getActivityFlows(activityId: string, language: string) {
  return protectedAxiosInstance.get(
    `/activities/get-activity-flows/${activityId}`,
    {
      params: { language },
    }
  );
}

export function getActivityLang(activityId: string) {
  return protectedAxiosInstance.get(
    `/activities/get-activity-lang/${activityId}`
  );
}

export function saveActivityResult(body: any) {
  return protectedAxiosInstance.post(`/activities/result/`, body);
}

export function updateActivityResult(resultId: string, body: any) {
  return protectedAxiosInstance.patch(`/activities/result/${resultId}`, body);
}

export function getActivityResult(resultId: string) {
  return protectedAxiosInstance.get(`/activities/result/${resultId}`);
}

export function getActivityResultHistory(moduleId: string) {
  return protectedAxiosInstance.get(`/activities/results/${moduleId}`);
}

export function submitPronunciationPractice(formData: FormData) {
  return protectedAxiosInstance.post(
    `/activities/pronunciation/practise`,
    formData
  );
}

export const getActivityResultHistoryUser = async (body: any) => {
  return protectedAxiosInstance.post(`/activities/by-user`, body);
};

export const getAssessmentHistoryUser = async (body: any) => {
  return protectedAxiosInstance.post(`/assesment-results/by-user`, body);
};

export function getTotalActivityCounts(activityId: string) {
  return protectedAxiosInstance.get(`/activities/count-results/${activityId}`);
}

export function markModuleAsCompleted(
  chapterId: string,
  bookId: string,
  learningPathId?: string,
  onBoardingBatchId?: string
) {
  return protectedAxiosInstance.post(
    `/document-result/mark-docs-completed/${chapterId}`,
    { bookId, learningPathId, onBoardingBatchId }
  );
}

export function markVideoAsCompleted(
  chapterId: string,
  bookId: string,
  learningPathId?: string,
  onBoardingBatchId?: string
) {
  // console.log({ chapterId, bookId });
  return protectedAxiosInstance.post(
    `/video-current-time/mark-video-completed/${chapterId}`,
    { bookId, learningPathId, onBoardingBatchId }
  );
}

export function logoutApi() {
  return protectedAxiosInstance.get(`/users/logout`);
}

export const getAllFeedbacks = (
  filter: any = {
    page: 1,
    limit: 12,
    search: "",
    userId: "",
  }
) => {
  return protectedAxiosInstance.get(`/manager-feedback/`, { params: filter });
};

export const getFeedback = (id: string, filters: any = {}) => {
  return protectedAxiosInstance.get(`/manager-feedback/${id}`, {
    params: filters,
  });
};

export const getAllFeedbackUsers = (filter: any) => {
  const params = {
    ...filter,
  };

  return protectedAxiosInstance.get(`/manager-feedback/users`, { params });
};

export const createFeedbackResult = (body: any) => {
  return protectedAxiosInstance.post(`/manager-feedback/result`, body);
};

export const createBulkFeedbackResult = (body: any[]) => {
  return protectedAxiosInstance.post(`/manager-feedback/bulk-result`, body);
};

export const getFeedbackResults = (id: string, filter: any) => {
  const params = {
    ...filter,
  };

  return protectedAxiosInstance.get(`/manager-feedback/results/${id}`, {
    params,
  });
};

export const userFeedbackResults = (id: string, filters: any) => {
  return protectedAxiosInstance.get(`/manager-feedback/user-results/${id}`, {
    params: filters,
  });
};

export const createChat = (body: any) => {
  return protectedAxiosInstance.post(`/chat/`, body);
};

export const getImageStatus = (chatId: string) => {
  return protectedAxiosInstance.get(`/chat/${chatId}/image-status`);
};

export const restartChatSession = () => {
  return protectedAxiosInstance.post(`/chat/restart`);
};

export const getChats = (filter: any) => {
  const params = {
    ...filter,
  };

  return protectedAxiosInstance.get(`/chat/`, {
    params,
  });
};

export const getFaqsApi = async (
  orgId: string
): Promise<{
  data: Faq[];
  error: string | null;
}> => {
  const response = await protectedAxiosInstance.get(`/faqs`, {
    params: { orgId },
  });
  return response.data;
};

export const getDataChats = (filter: any) => {
  const params = {
    ...filter,
  };
  return protectedAxiosInstance.get(`/chatwithdata/`, {
    params,
  });
};

export const getGlobalBotSettingsInfo = () => {
  return protectedAxiosInstance.get(`/chat/get-bot-settings`);
};

export const getPreviewAudio = (body: any) => {
  return protectedAxiosInstance.post("/ai-examiner/generate_preview", body);
};

export const getSheets = () => {
  return protectedAxiosInstance.get("/chatwithdata/sheets");
};

export const getCWDResponse = (body: any) => {
  return protectedAxiosInstance.post("/chatwithdata/generate-duckdb", body);
};

export const globalBotDuckDb = (body: any) => {
  return protectedAxiosInstance.post("/chatwithdata/global-bot-duckdb", body);
};

export const getCWDChats = (filter: any, sheetId: string) => {
  const params = {
    ...filter,
    sheetId,
  };

  return protectedAxiosInstance.get(`/chatwithdata/`, {
    params,
  });
};

export const getMappedCustomer = (params: any) => {
  return protectedAxiosInstance.get("/customer/all", { params });
};

export const checkPptCoachResume = ({
  chapterId,
  userId,
}: {
  chapterId: string;
  userId: string;
}) => {
  return protectedAxiosInstance.post("/roleplay/coach/ppt-resume", {
    chapterId,
    userId,
  });
};

export const getSlideIntro = (body: any) => {
  return protectedAxiosInstance.post("/bot/getSlideIntro", body);
};

export const sendGlobalBotChatFeedback = (body: any) => {
  return protectedAxiosInstance.post("/chat/globalbot-chat-feedback", body);
};

export const getAssignmentDetails = async (
  assignmentId: string,
  queryParams?: any
) => {
  return protectedAxiosInstance.get(`/assignments/details/${assignmentId}`, {
    params: queryParams,
  });
};

export const getAssignment = async (assignmentId: string) => {
  return protectedAxiosInstance.get(`/assignments/${assignmentId}`);
};

// ------------------------- Video Interactions Endpoints START-------------------------

export async function initInteractionResults(
  bookId: string,
  interactionId: string,
  chapterId: string,
  lang: string,
  learningPathId?: string | null,
  onBoardingBatchId?: string | null
) {
  const response = await protectedAxiosInstance.post(
    "/video-interaction/results/init",
    {
      bookId,
      interactionId,
      chapterId,
      lang,
      learningPathId,
      onBoardingBatchId,
    }
  );
  return response.data;
}

export async function endInteractionResults(
  bookId: string,
  interactionId: string,
  chapterId: string,
  lang: string,
  interactionData?: InteractionData
) {
  return protectedAxiosInstance.post("/video-interaction/results/end", {
    bookId,
    interactionId,
    chapterId,
    lang,
    ...interactionData,
  });
}

export async function getInteractionHistory(
  interactionId: string,
  lang?: string,
  page?: number,
  limit?: number
) {
  const response = await protectedAxiosInstance.get(
    `/video-interaction/${interactionId}/history`,
    {
      params: {
        ...(lang && { lang }),
        ...(page && { page }),
        ...(limit && { limit }),
      },
    }
  );
  return response.data;
}

// TODO: Improve the endpoint
export async function getCompleteInteractionHistory(
  chapterId: string,
  interactionId: string,
  interactionResultId: string,
  lang: string
) {
  const response = await protectedAxiosInstance.get(
    `/video-interaction/${chapterId}/${interactionId}/${interactionResultId}/${lang}/result`
  );
  return response.data;
}

export async function getUserVideoStats(
  bookId: string,
  chapterId: string,
  lang: string
) {
  const response = await protectedAxiosInstance.get(
    `/video-interaction/user-stats/${bookId}/${chapterId}?lang=${lang}`
  );
  return response.data;
}

export async function saveUserVideoStats(body: {
  bookId: string;
  chapterId: string;
  language: string;
  watchedDuration: number;
  totalDuration: number;
  learningPathId?: string;
  onBoardingBatchId?: string;
}) {
  const response = await protectedAxiosInstance.post(
    "/video-interaction/user-stats/save",
    body
  );
  return response.data;
}

export async function startModuleTimeSession(body: {
  bookId: string;
  chapterId: string;
  moduleType: "video" | "document";
}) {
  const response = await protectedAxiosInstance.post(
    "/module-time/start",
    body
  );
  return response.data;
}

export function updateModuleTimeSession(
  sessionId: string,
  action: "heartbeat" | "resume" | "pause" | "end"
) {
  return protectedAxiosInstance.post(`/module-time/${sessionId}/${action}`);
}

export function endModuleTimeSessionOnPageHide(sessionId: string) {
  const token = localStorage.getItem("token");
  if (!token) return;

  void fetch(
    `${String(baseURL || "").replace(/\/$/, "")}/module-time/${sessionId}/end`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: "{}",
      keepalive: true,
    }
  ).catch(() => undefined);
}

// ------------------------- Video Interactions Endpoints END-------------------------

// Mple Avatar Session Tracking
// anonymous: widget, demo, SCORM and interview visitors have no login token,
// and protectedAxiosInstance would send them to /signin. The session
// endpoints accept requests without a token.
const mpleAvatarSessionClient = (anonymous: boolean) =>
  anonymous ? globalAxiosInstance : protectedAxiosInstance;

export const createMpleAvatarSession = (
  body: {
    roleplayId: string;
    chapterId?: string;
    userId?: string;
    userName?: string;
    organizationId?: string;
    avatarId: string;
    startTime: string;
    status: "inprogress";
    backend?: MpleAvatarBackend;
    source?: MpleAvatarSessionSource;
    resultId?: string;
  },
  anonymous = false
) => {
  return mpleAvatarSessionClient(anonymous).post(
    `/roleplay/mple-avatar-session`,
    body
  );
};

/** The backend stamps lastSeenAt with its own clock on every update. */
export const heartbeatMpleAvatarSession = (
  sessionId: string,
  body: { resultId?: string },
  anonymous = false
) => {
  return mpleAvatarSessionClient(anonymous).patch(
    `/roleplay/mple-avatar-session/${sessionId}`,
    body
  );
};

/** Closes the session record while the page unloads; axios would be cancelled. */
export function endMpleAvatarSessionOnPageHide(sessionId: string) {
  const token = localStorage.getItem("token");

  void fetch(
    `${String(baseURL || "").replace(/\/$/, "")}/roleplay/mple-avatar-session/${sessionId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        status: "completed",
        endTime: new Date().toISOString(),
        endReason: "page_closed",
      }),
      keepalive: true,
    }
  ).catch(() => undefined);
}

export const updateMpleAvatarSession = (
  sessionId: string,
  body: {
    status?: "connected" | "completed" | "failed";
    connectedTime?: string;
    startupTime?: number;
    endTime?: string;
    totalTime?: number;
    errorMessage?: string;
    resultId?: string;
    region?: string;
    provider?: string;
    endReason?: MpleAvatarEndReason;
    endDetail?: string;
  },
  anonymous = false
) => {
  return mpleAvatarSessionClient(anonymous).patch(
    `/roleplay/mple-avatar-session/${sessionId}`,
    body
  );
};

export const getOrg = async (orgId: string) => {
  return protectedAxiosInstance.get(`/organizations/${orgId}`);
};

export const getKpiData = async (
  selectedEmployeeId?: string,
  selectedYear?: number
) => {
  const params: any = {};
  if (selectedEmployeeId) params.selectedEmployeeId = selectedEmployeeId;
  if (selectedYear) params.selectedYear = selectedYear;

  return protectedAxiosInstance.get(`/kpi-dashboard`, { params });
};

export interface AssignedAgentForMe {
  _id: string;
  name: string;
  role?: string;
  memoryType?: string;
}

export interface StartAgentConverseResponse {
  runId: string;
  callSessionId: string;
  websocketUrl: string;
  audio: {
    inputSampleRate: 16000;
    outputSampleRate: 24000;
    encoding: "pcm16";
  };
}

export interface AgentGatherResponse {
  runId: string;
  gatherOutput: string;
  actualData?: Array<{
    query?: string;
    sheetTitle?: string;
    rows?: Record<string, unknown>[];
  }>;
  visualizationPanels?: Array<Record<string, any>>;
  sections?: Array<{
    query?: string;
    sheetTitle?: string;
    rows?: Record<string, unknown>[];
    panels?: Array<Record<string, any>>;
  }>;
}

export const getAssignedAgentsForMe = () => {
  return protectedAxiosInstance.get<AssignedAgentForMe[]>(
    `/agents/me/assigned`
  );
};

export const startUserAgentGather = (agentId: string, signal?: AbortSignal) => {
  return protectedAxiosInstance.post<AgentGatherResponse>(
    `/agents/${agentId}/gather/start-user`,
    undefined,
    { signal }
  );
};

export const startUserAgentConverseFromRun = (runId: string) => {
  return protectedAxiosInstance.post<StartAgentConverseResponse>(
    `/agents/runs/${runId}/converse/start-user-from-run`
  );
};

export const startUserAgentLiveConverse = (agentId: string) => {
  return protectedAxiosInstance.post<StartAgentConverseResponse>(
    `/agents/${agentId}/converse/live/start-user`
  );
};

export const postTrainingFeedbackApi = async (body: {
  bookId?: string;
  rating?: number;
  overallRating?: string[];
  comments?: string;
}) => {
  return await protectedAxiosInstance.post("/training-feedbacks", body);
};
